// ===================================================================
// PA GRANTS — Cross-Project Timeline
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Modal, ActionIcon, Loader, Center, Textarea,
} from '../../components/gab/GabComponents';
import { IconPlus, IconEdit, IconTrash, IconSearch, IconRefresh, IconClock, IconCalendar } from '@tabler/icons-react';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { notifications } from '../../lib/notifications';
import { fmtDate } from '../../lib/date-format';

const EVENT_TYPE_OPTIONS = [
  { value: 'incident_declaration', label: 'Incident Declaration' },
  { value: 'project_submitted', label: 'Project Submitted' },
  { value: 'project_approved', label: 'Project Approved' },
  { value: 'obligation', label: 'Obligation' },
  { value: 'work_start', label: 'Work Start' },
  { value: 'work_complete', label: 'Work Complete' },
  { value: 'closeout_initiated', label: 'Closeout Initiated' },
  { value: 'closeout_complete', label: 'Closeout Complete' },
  { value: 'appeal_filed', label: 'Appeal Filed' },
  { value: 'appeal_decision', label: 'Appeal Decision' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_COLORS: Record<string, string> = {
  pending: '#939598', in_progress: '#C68700', completed: 'var(--color-success)', overdue: 'var(--color-error)', cancelled: '#939598',
};

const TYPE_COLORS: Record<string, string> = {
  incident_declaration: 'var(--color-error)', obligation: 'var(--color-success)', work_start: 'var(--color-primary-700)', work_complete: '#03AC9E',
  closeout_complete: 'var(--data-viz-650)', appeal_filed: '#F86A0B', deadline: 'var(--color-error)', milestone: 'var(--data-viz-100)',
};

const EMPTY_FORM = {
  title: '', eventType: 'milestone', description: '', eventDate: '', status: 'pending',
};

export default function Timeline() {
  const {
    timelineEvents, fetchTimeline, createTimeline, updateTimelineEvent, deleteTimelineEvent,
    projects, fetchProjects,
  } = usePAGrantsStore();

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []); // eslint-disable-line

  async function load() {
    setLoading(true);
    await Promise.all([fetchTimeline(), fetchProjects()]);
    setLoading(false);
  }

  const projectMap = useMemo(() => {
    const m: Record<number, string> = {};
    projects.forEach((p: any) => { m[p.Rid] = p.projectNumber || p.title || `Project ${p.Rid}`; });
    return m;
  }, [projects]);

  const filtered = useMemo(() => {
    let list = [...(timelineEvents as any[])].sort((a, b) => {
      const da = a.eventDate ? new Date(a.eventDate).getTime() : 0;
      const db = b.eventDate ? new Date(b.eventDate).getTime() : 0;
      return da - db;
    });
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        (r.title || '').toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q)
      );
    }
    if (typeFilter) list = list.filter(r => r.eventType === typeFilter);
    return list;
  }, [timelineEvents, search, typeFilter]);

  function openAdd() {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setModalOpen(true);
  }

  function openEdit(r: any) {
    setEditingId(r.Rid);
    setForm({
      title: r.title || '', eventType: r.eventType || 'milestone',
      description: r.description || '',
      eventDate: r.eventDate ? r.eventDate.substring(0, 10) : '',
      status: r.status || 'pending',
    });
    setModalOpen(true);
  }

  async function handleSave() {
    if (!form.title) { notifications.show({ message: 'Title required', color: 'var(--color-error)' }); return; }
    setSaving(true);
    try {
      const data = {
        title: form.title, eventType: form.eventType,
        description: form.description,
        eventDate: form.eventDate ? `${form.eventDate}T00:00:00Z` : '',
        status: form.status,
      };
      if (editingId) {
        await updateTimelineEvent(editingId, data);
      } else {
        await createTimeline(data);
      }
      notifications.show({ message: editingId ? 'Updated' : 'Added', color: 'var(--color-success)' });
      setModalOpen(false);
    } catch {
      notifications.show({ message: 'Failed to save', color: 'var(--color-error)' });
    }
    setSaving(false);
  }

  async function handleDelete(r: any) {
    if (!confirm(`Delete "${r.title}"?`)) return;
    await deleteTimelineEvent(r.Rid);
    notifications.show({ message: 'Deleted', color: '#939598' });
  }

  const today = Date.now();
  const upcoming = filtered.filter((r: any) => r.eventDate && new Date(r.eventDate).getTime() >= today && r.status !== 'completed');
  const overdue = filtered.filter((r: any) => r.eventDate && new Date(r.eventDate).getTime() < today && r.status !== 'completed' && r.status !== 'cancelled');

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">Project Timeline</Title>
        <Button variant="primary" size="sm" leftIcon={<IconPlus size={14} />} onClick={openAdd}>
          Add Event
        </Button>
      </Group>

      {overdue.length > 0 && (
        <Paper p="sm" radius="md" style={{ background: '#fff5f5', border: '1px solid #ffc9c9' }}>
          <Group gap="xs">
            <IconClock size={16} color="var(--color-error)" />
            <Text size="sm" fw={600} c="var(--color-error)">{overdue.length} overdue event{overdue.length !== 1 ? 's' : ''}</Text>
            <Text size="sm" c="dimmed">— {overdue.map((r: any) => r.title).join(', ')}</Text>
          </Group>
        </Paper>
      )}

      <Paper p="md" radius="md" withBorder>
        <Group gap="sm" mb="md">
          <TextInput
            placeholder="Search events…"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1 }}
            size="sm"
          />
          <Select
            placeholder="All types"
            data={EVENT_TYPE_OPTIONS}
            value={typeFilter}
            onChange={setTypeFilter}
            clearable size="sm"
            style={{ width: 200 }}
          />
          <ActionIcon variant="subtle" size="md" onClick={load} title="Refresh">
            <IconRefresh size={16} />
          </ActionIcon>
        </Group>

        {loading ? (
          <Center py="xl"><Loader /></Center>
        ) : filtered.length === 0 ? (
          <Stack align="center" py="xl" gap="sm">
            <IconCalendar size={40} color="var(--color-neutral-400)" />
            <Text c="dimmed">No timeline events found.</Text>
            <Button size="sm" variant="light" leftIcon={<IconPlus size={14} />} onClick={openAdd}>
              Add First Event
            </Button>
          </Stack>
        ) : (
          <Stack gap={0}>
            {filtered.map((r: any, idx: number) => {
              const isOverdue = r.eventDate && new Date(r.eventDate).getTime() < today && r.status !== 'completed' && r.status !== 'cancelled';
              return (
                <Group key={r.Rid} gap="md" align="flex-start" wrap="nowrap" py="sm">
                  {/* Timeline node */}
                  <Stack align="center" gap={0} style={{ minWidth: 40 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: r.status === 'completed' ? 'var(--color-success)' :
                        isOverdue ? 'var(--color-error)' :
                        r.status === 'in_progress' ? '#f08c00' : '#adb5bd',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <IconClock size={14} color="white" />
                    </div>
                    {idx < filtered.length - 1 && (
                      <div style={{ width: 2, flexGrow: 1, minHeight: 20, background: '#dee2e6', margin: '2px 0' }} />
                    )}
                  </Stack>

                  {/* Content */}
                  <Paper p="sm" withBorder style={{ flex: 1, marginBottom: idx < filtered.length - 1 ? 4 : 0 }}>
                    <Group justify="space-between" mb={4}>
                      <Group gap="xs" wrap="wrap">
                        <Text fw={600} size="sm">{r.title}</Text>
                        <Badge size="xs" variant="light" color={TYPE_COLORS[r.eventType] || '#939598'}>
                          {(r.eventType || '').replace(/_/g, ' ')}
                        </Badge>
                        <Badge size="xs" variant="light" color={isOverdue ? 'var(--color-error)' : (STATUS_COLORS[r.status] || '#939598')}>
                          {isOverdue ? 'Overdue' : (r.status || 'pending').replace(/_/g, ' ')}
                        </Badge>
                        {r.projectId && (
                          <Badge size="xs" variant="outline">{projectMap[r.projectId] || `#${r.projectId}`}</Badge>
                        )}
                      </Group>
                      <Group gap={4}>
                        <ActionIcon variant="subtle" size="sm" onClick={() => openEdit(r)}>
                          <IconEdit size={12} />
                        </ActionIcon>
                        <ActionIcon variant="subtle" size="sm" color="var(--color-error)" onClick={() => handleDelete(r)}>
                          <IconTrash size={12} />
                        </ActionIcon>
                      </Group>
                    </Group>
                    {r.eventDate && (
                      <Text size="xs" c="dimmed">
                        <IconCalendar size={10} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                        {fmtDate(r.eventDate)}
                      </Text>
                    )}
                    {r.description && <Text size="xs" c="dimmed" mt={4}>{r.description}</Text>}
                  </Paper>
                </Group>
              );
            })}
          </Stack>
        )}
      </Paper>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? 'Edit Timeline Event' : 'Add Timeline Event'}>
        <Stack gap="sm">
          <TextInput label="Event Title" required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          <Select label="Event Type" data={EVENT_TYPE_OPTIONS} value={form.eventType} onChange={v => setForm(f => ({ ...f, eventType: v || 'milestone' }))} />
          <TextInput label="Date" type="date" value={form.eventDate} onChange={e => setForm(f => ({ ...f, eventDate: e.target.value }))} />
          <Select label="Status" data={STATUS_OPTIONS} value={form.status} onChange={v => setForm(f => ({ ...f, status: v || 'pending' }))} />
          <Textarea label="Description" value={form.description} onChange={(e: any) => setForm(f => ({ ...f, description: e.target.value }))} rows={3} />
          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleSave}>Save</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
